import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Row, Col, Form, Table } from 'react-bootstrap'
import DownloadTextResponseModalContainer from './DownloadTextResponseModalContainer'
import Loader from './Loader'
import Message from './Message'

import {
  getAllPartiList,
  downloadTextResp,
  fetchMyActiveExportJob,
  resumeExportJobPoll,
} from '../actions/participantActions'

import {
  ALL_PARTI_LIST_RESET,
  DOWNLOAD_TEXT_RESP_RESET,
} from '../constants/participantConstants'

export const DownloadTextResponse = ({
  studyId,
  selectedStage,
  selectedGroup,
}) => {
  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const downloadTextRespState = useSelector((state) => state.downloadTextResp)
  const {
    error: errorDownloadTextResp,
    loading: loadingDownloadTextResp,
    success: successDownloadTextResp,
  } = downloadTextRespState

  const allPartiList = useSelector((state) => state.allPartiList)
  const {
    participantsData: allParticipantsData,
    error: errorGetAllPartiList,
    loading: loadingGetAllPartiList,
    success: successGetAllPartiList,
  } = allPartiList

  const dispatch = useDispatch()

  const [show, setShow] = useState(false)

  const [warningText, setWarningText] = useState('')

  const [excludedParticipantsIds, setExcludedParticipantsIds] = useState([])

  const [keepDuplicate, setKeepDuplicate] = useState(false)

  const [activeExportJob, setActiveExportJob] = useState(null)
  const [loadingActiveExportJob, setLoadingActiveExportJob] = useState(false)

  const userHasActiveExport =
    activeExportJob &&
    (activeExportJob.status === 'pending' ||
      activeExportJob.status === 'running')

  // Hide the completed "ready to download" banner while a new export is in
  // flight (footer already shows progress), or if the resolvable job is active.
  const userHasAwaitingDownload =
    Boolean(activeExportJob?.awaitingDownload) &&
    !loadingDownloadTextResp &&
    !userHasActiveExport

  const formatExcludedParticipants = (job) => {
    if (!job.excludedParticipants || job.excludedParticipants.length === 0) {
      return 'None'
    }
    return job.excludedParticipants
      .map(
        (participant) =>
          `${participant.participantCode || participant.id}${
            participant.email ? ` (${participant.email})` : ''
          }`
      )
      .join(', ')
  }

  const loadActiveExportJob = async () => {
    setLoadingActiveExportJob(true)
    try {
      const job = await dispatch(fetchMyActiveExportJob(studyId))
      setActiveExportJob(job)
    } catch (error) {
      setActiveExportJob(null)
    } finally {
      setLoadingActiveExportJob(false)
    }
  }

  // For shift select or deselect
  const [lastUpdatedParticipantInd, setLastUpdatedParticipantInd] = useState(0) // ind stands for the index of the current retrieved participants

  useEffect(() => {
    if (successDownloadTextResp) {
      dispatch({ type: DOWNLOAD_TEXT_RESP_RESET })
      setActiveExportJob(null)
    }
  }, [dispatch, successDownloadTextResp])

  // Clear cached all-participant list when navigating to another study so the
  // modal never shows the previous study's participants.
  useEffect(() => {
    dispatch({ type: ALL_PARTI_LIST_RESET })
    setExcludedParticipantsIds([])
    setLastUpdatedParticipantInd(0)
    setWarningText('')
    setKeepDuplicate(false)
    setActiveExportJob(null)
  }, [dispatch, studyId])

  useEffect(() => {
    if (!(loadingDownloadTextResp && show)) {
      return
    }

    let cancelled = false

    const refreshInProgressJob = async () => {
      setLoadingActiveExportJob(true)
      try {
        const job = await dispatch(fetchMyActiveExportJob(studyId))
        if (cancelled) {
          return
        }
        // Only surface pending/running while a download workflow is active —
        // do not re-show a completed awaiting-download banner mid-flight.
        if (
          job &&
          (job.status === 'pending' || job.status === 'running')
        ) {
          setActiveExportJob(job)
        }
      } catch (error) {
        // Keep whatever state we have; poll / fail path will refresh.
      } finally {
        if (!cancelled) {
          setLoadingActiveExportJob(false)
        }
      }
    }

    refreshInProgressJob()

    return () => {
      cancelled = true
    }
  }, [loadingDownloadTextResp, show, studyId, dispatch])

  const updateParticipantSelection = (
    tempExcludedParticipantsIds,
    checked,
    participantId
  ) => {
    if (checked) {
      if (!tempExcludedParticipantsIds.find((x) => x === participantId)) {
        tempExcludedParticipantsIds.push(participantId)
      }
    } else {
      tempExcludedParticipantsIds = tempExcludedParticipantsIds.filter(
        (x) => x !== participantId
      )
    }
    return tempExcludedParticipantsIds
  }

  const checkParticipantHandler = (e, ind, participants) => {
    let tempExcludedParticipantsIds = [...excludedParticipantsIds]

    if (e.nativeEvent.shiftKey) {
      // For bulk selection when pressing shift key
      if (ind >= lastUpdatedParticipantInd) {
        for (let i = lastUpdatedParticipantInd; i <= ind; ++i) {
          tempExcludedParticipantsIds = updateParticipantSelection(
            tempExcludedParticipantsIds,
            e.target.checked,
            participants[i]._id
          )
        }
      } else {
        for (let i = lastUpdatedParticipantInd; i >= ind; --i) {
          tempExcludedParticipantsIds = updateParticipantSelection(
            tempExcludedParticipantsIds,
            e.target.checked,
            participants[i]._id
          )
        }
      }
    } else {
      // For single selection
      tempExcludedParticipantsIds = updateParticipantSelection(
        tempExcludedParticipantsIds,
        e.target.checked,
        participants[ind]._id
      )
    }

    setLastUpdatedParticipantInd(ind)
    setExcludedParticipantsIds(tempExcludedParticipantsIds)
    if (tempExcludedParticipantsIds.length > 0) {
      setWarningText(
        `(${tempExcludedParticipantsIds.length} participant(s) excluded)`
      )
    } else {
      setWarningText('')
    }
  }

  const handleOpen = () => {
    let now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      const cachedStudyId =
        allParticipantsData?.study?._id || allParticipantsData?.study
      const listMatchesStudy =
        successGetAllPartiList &&
        cachedStudyId &&
        String(cachedStudyId) === String(studyId)

      if (!loadingGetAllPartiList && !listMatchesStudy) {
        setExcludedParticipantsIds([])
        setLastUpdatedParticipantInd(0)
        setWarningText('')
        dispatch(getAllPartiList(studyId))
      }
      loadActiveExportJob()
    }
    setShow(true)
  }

  const handleDownload = async () => {
    // Drop the completed-job banner as soon as a new workflow starts.
    if (activeExportJob?.awaitingDownload) {
      setActiveExportJob(null)
    }

    const result = await dispatch(
      downloadTextResp(
        studyId,
        selectedStage,
        selectedGroup,
        excludedParticipantsIds,
        !keepDuplicate
      )
    )
    if (result?.userOwnActiveJob && result.job) {
      setActiveExportJob(result.job)
    } else if (result?.success) {
      setActiveExportJob(null)
    } else {
      await loadActiveExportJob()
    }
  }

  const handleContinueWaiting = async () => {
    if (!activeExportJob?.jobId) {
      return
    }
    const result = await dispatch(
      resumeExportJobPoll(activeExportJob.jobId)
    )
    if (result?.success) {
      setActiveExportJob(null)
    } else {
      await loadActiveExportJob()
    }
  }

  const handleClose = () => {
    if (!loadingDownloadTextResp) {
      setShow(false)
    }
  }

  return (
    <>
      <Button variant='primary' className='mt-2 mr-2' onClick={handleOpen}>
        Download Text Response
      </Button>

      <DownloadTextResponseModalContainer
        title='Download Text Response in the Selected Stage and Participant Group'
        show={show}
        showClose={!loadingDownloadTextResp}
        handleDownload={handleDownload}
        handleClose={handleClose}
        warningText={warningText}
        isLoading={loadingDownloadTextResp}
        loadingMessage='Export in progress. Please keep this window open until the download starts.'
        downloadDisabled={userHasActiveExport}
        downloadButtonLabel={
          userHasActiveExport
            ? 'Export already in progress'
            : 'Download Text Responses'
        }
      >
        {errorDownloadTextResp && (
          <Message variant='danger'>{errorDownloadTextResp}</Message>
        )}
        {errorGetAllPartiList && (
          <Message variant='danger'>{errorGetAllPartiList}</Message>
        )}

        {loadingActiveExportJob ? (
          <Loader />
        ) : (
          (userHasActiveExport || userHasAwaitingDownload) && (
            <Message variant={userHasAwaitingDownload ? 'success' : 'info'}>
              {userHasAwaitingDownload ? (
                <p>
                  <strong>Your export is ready to download.</strong> The
                  download link remains available for 24 hours after the export
                  completed
                  {activeExportJob.downloadExpiresAt
                    ? ` (until ${new Date(
                        activeExportJob.downloadExpiresAt
                      ).toLocaleString()})`
                    : ''}
                  . You can start a new export for this study anytime — you do
                  not need to wait for the link to expire.
                </p>
              ) : (
                <p>
                  <strong>You have an export in progress for this study.</strong>{' '}
                  Only one export can run at a time per study. You can still
                  export other studies concurrently. If you close this window
                  and log in again, this export will still be shown here
                  (including if you joined another user&apos;s same selection).
                </p>
              )}
              {activeExportJob.studyName && (
                <p>
                  <strong>Study:</strong> {activeExportJob.studyName}
                </p>
              )}
              {activeExportJob.stageName && (
                <p>
                  <strong>Stage:</strong> {activeExportJob.stageName}
                </p>
              )}
              {activeExportJob.groupName && (
                <p>
                  <strong>Group:</strong> {activeExportJob.groupName}
                </p>
              )}
              <p>
                <strong>Excluded participants:</strong>{' '}
                {formatExcludedParticipants(activeExportJob)}
              </p>
              <p>
                <strong>Keep Duplicate Data:</strong>{' '}
                {activeExportJob.keepDuplicateData ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Status:</strong> {activeExportJob.status}
              </p>
              {!loadingDownloadTextResp && (
                <Button
                  variant='primary'
                  size='sm'
                  className='mt-2'
                  onClick={handleContinueWaiting}
                >
                  {userHasAwaitingDownload
                    ? 'Download now'
                    : 'Continue waiting for download'}
                </Button>
              )}
            </Message>
          )
        )}

        {loadingGetAllPartiList ? (
          <Loader />
        ) : (
          !logout &&
          allParticipantsData &&
          allParticipantsData.participants && (
            <>
              {allParticipantsData.participants.length > 0 ? (
                <>
                  <p>
                    Export runs in the background. Each user can run one export
                    at a time per study (other studies can export concurrently).
                    If another user is exporting the same stage, group, and
                    participant selection, you join that export and wait for it
                    to finish. Different participant selections for the same
                    study are separate jobs. After completion, the download link
                    stays available for 24 hours; starting a new export does not
                    require waiting for that window to expire.
                  </p>
                  <p>
                    The table below lists all the participants of this study,
                    including ones who had not submitted any data in the
                    selected participant group. If you do not want to export all
                    the data at a time, you can select participants below to{' '}
                    <span style={{ color: 'red' }}>exclude</span> them from the
                    exported data.
                  </p>
                  <p>
                    Tips: You can hold the 'Shift' key and check/uncheck a
                    participant to perform bulk selection/deselection. Bulk
                    selection/deselection will be applied from the last
                    selected/deselected participant to the current
                    selected/deselected participant.
                  </p>
                  <Row>
                    <Col className='d-flex justify-content-end'>
                      <Form.Group controlId={`keep_duplicate_checkbox`}>
                        <Form.Check
                          type='checkbox'
                          checked={keepDuplicate}
                          // for checkbox it is e.target.checked
                          onChange={(e) => {
                            setKeepDuplicate(e.target.checked)
                          }}
                          label='Keep Duplicate Data'
                        ></Form.Check>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    className='table-sm'
                    style={{ marginTop: '10px' }}
                  >
                    <thead>
                      <tr>
                        <th></th>
                        <th>PARTICIPANT ID</th>
                        <th>EMAIL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Particpants here are with participation filtered */}
                      {allParticipantsData.participants.map(
                        (participant, ind) => (
                          <tr key={participant._id}>
                            <td className='align-middle'>
                              <Form.Group
                                controlId={`${participant._id}_select_checkbox`}
                              >
                                <Form.Check
                                  type='checkbox'
                                  checked={excludedParticipantsIds.includes(
                                    participant._id
                                  )}
                                  // for checkbox it is e.target.checked
                                  onChange={(e) => {
                                    checkParticipantHandler(
                                      e,
                                      ind,
                                      allParticipantsData.participants
                                    )
                                  }}
                                ></Form.Check>
                              </Form.Group>
                            </td>
                            <td className='align-middle'>
                              {participant.participant_code}
                            </td>
                            <td className='align-middle'>
                              {participant.email}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </>
              ) : (
                <Row className='align-items-center my-3'>
                  <Col>
                    <h4>No participant has enroled this study</h4>
                  </Col>
                </Row>
              )}
            </>
          )
        )}
      </DownloadTextResponseModalContainer>
    </>
  )
}

export default DownloadTextResponse
